import { useState, useEffect, useCallback } from 'react';
import { countryAPI } from '../services/api';
import toast from 'react-hot-toast';

const BLANK = {
  // New countries start INACTIVE: "Active" opens sign-ups in that country.
  code: '', name: '', flag: '', dialCode: '', isActive: false, isDefault: false, sortOrder: 0,
  company: { name: '', tagline: '', logo: '', website: '', founded: '', regNumber: '' },
  office: { street: '', city: '', district: '', country: '', postalCode: '', mapUrl: '' },
  contact: { phone: '', altPhone: '', email: '', supportEmail: '', whatsapp: '' },
  timezone: 'Africa/Kampala',
  currency: { code: '', name: '', symbol: '', decimals: 0 },
  exchange: { buySpreadPct: 5, sellSpreadPct: 5, minBuyUsdt: 5, maxBuyUsdt: 10000, minSellUsdt: 5, maxSellUsdt: 10000, withdrawalFeePct: 3.5, enabled: true },
  referral: { bonusPct: 2, maxLevels: 1, minDeposit: 5000, enabled: true },
  tax: { vatRate: 0, withholdingRate: 0, vatRegistered: false, vatNumber: '' },
  branding: { primaryColor: '#0A1628', accentColor: '#00A8DB', secondaryColor: '#1B61AC', bgColor: '#F0F6FF', appName: 'DWI FINTECH' },
  mobileMoneyProviders: [],
  paymentProviders: [],
  socialLinks: [],
  footerText: '',
  languages: [{ code: 'en', name: 'English', isDefault: true }],
};

const S = {
  wrap: { minHeight: '100vh', background: '#0A1628', padding: '24px', fontFamily: 'Inter, system-ui, sans-serif', color: '#F0F6FF' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
  title: { fontSize: '22px', fontWeight: 900, color: '#F0F6FF', margin: 0 },
  sub: { fontSize: '13px', color: 'rgba(232,239,248,0.4)', marginTop: '2px' },
  card: { background: 'rgba(13,28,53,0.8)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px' },
  label: { display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(232,239,248,0.4)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F6FF', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: '#00A8DB', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '16px 0 10px' },
  badge: (active) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 700,
    background: active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
    color: active ? '#10B981' : '#f87171', border: `1px solid ${active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.25)'}`,
  }),
  btn: (color = '#00A8DB') => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: '13px', color: color === 'red' ? '#f87171' : '#0A1628',
    background: color === 'red' ? 'rgba(239,68,68,0.15)' : color === 'ghost' ? 'rgba(255,255,255,0.06)' : `linear-gradient(135deg,#1B61AC,${color})`,
    border: color === 'red' ? '1px solid rgba(239,68,68,0.3)' : color === 'ghost' ? '1px solid rgba(255,255,255,0.1)' : 'none',
  }),
};

function flagFromCode(code) {
  if (!code || code.length !== 2) return '';
  const offset = 127397;
  return String.fromCodePoint(code.toUpperCase().charCodeAt(0) + offset) +
         String.fromCodePoint(code.toUpperCase().charCodeAt(1) + offset);
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      <input style={S.input} type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <label style={{ ...S.label, margin: 0 }}>{label}</label>
      <button type="button" onClick={() => onChange(!value)}
        style={{
          width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
          background: value ? '#00A8DB' : 'rgba(255,255,255,0.12)',
          position: 'relative', transition: 'background 0.2s',
        }}>
        <span style={{
          position: 'absolute', top: '3px', left: value ? '21px' : '3px',
          width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', display: 'block',
        }} />
      </button>
    </div>
  );
}

export default function CountriesAdmin() {
  const [countries, setCountries]   = useState([]);
  const [selected, setSelected]     = useState(null);   // country being edited
  const [form, setForm]             = useState(BLANK);
  const [mode, setMode]             = useState('list'); // 'list' | 'edit' | 'create'
  const [saving, setSaving]         = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  const load = useCallback(async () => {
    try {
      const res = await countryAPI.adminList();
      setCountries(res.countries || []);
    } catch { toast.error('Failed to load countries'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (c) => {
    try {
      const res = await countryAPI.adminGet(c._id);
      const full = res.country;
      setSelected(full);
      setForm({ ...BLANK, ...full });
      setMode('edit');
      setActiveSection('basic');
    } catch { toast.error('Failed to load country details'); }
  };

  const openCreate = () => {
    setSelected(null);
    setForm({ ...BLANK });
    setMode('create');
    setActiveSection('basic');
  };

  const setF = (path, val) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.code || !form.name) { toast.error('Country code and name are required'); return; }
    setSaving(true);
    try {
      if (mode === 'create') {
        await countryAPI.create(form);
        toast.success('Country created');
      } else {
        await countryAPI.update(selected._id, form);
        toast.success('Country updated');
      }
      setMode('list');
      load();
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (c) => {
    if (!c.isActive && !window.confirm(`Activate ${c.name}? This opens sign-ups: ${c.name} will appear on the sign-up page and people can register in it.`)) return;
    try {
      await countryAPI.setActive(c._id, !c.isActive);
      toast.success(`Country ${c.isActive ? 'deactivated' : 'activated'}`);
      load();
    } catch (err) { toast.error(err.message || 'Failed to toggle'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await countryAPI.remove(c._id);
      toast.success('Country deleted');
      load();
    } catch (err) { toast.error(err?.message || 'Delete failed'); }
  };

  const SECTIONS = ['basic', 'company', 'office', 'contact', 'currency', 'exchange', 'referral', 'branding', 'tax'];

  const SectionNav = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '20px' }}>
      {SECTIONS.map(s => (
        <button key={s} type="button" onClick={() => setActiveSection(s)}
          style={{
            padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: 700,
            background: activeSection === s ? '#00A8DB' : 'rgba(255,255,255,0.06)',
            color: activeSection === s ? '#0A1628' : 'rgba(232,239,248,0.6)',
          }}>
          {s.charAt(0).toUpperCase() + s.slice(1)}
        </button>
      ))}
    </div>
  );

  // ── Form sections ─────────────────────────────────────────────────────────
  const renderSection = () => {
    if (activeSection === 'basic') return (
      <>
        <div style={S.row}>
          <Field label="Country Code (e.g. UG)" value={form.code} onChange={v => {
            setF('code', v.toUpperCase());
            if (v.length === 2 && !form.flag) setF('flag', flagFromCode(v));
          }} placeholder="UG" />
          <Field label="Country Name" value={form.name} onChange={v => setF('name', v)} placeholder="Uganda" />
        </div>
        <div style={S.row}>
          <Field label="Flag Emoji" value={form.flag} onChange={v => setF('flag', v)} placeholder="🇺🇬" />
          <Field label="Dial Code" value={form.dialCode} onChange={v => setF('dialCode', v)} placeholder="+256" />
        </div>
        <div style={S.row}>
          <Field label="Sort Order" type="number" value={form.sortOrder} onChange={v => setF('sortOrder', +v)} />
          <div />
        </div>
        <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
          <Toggle label="Active" value={form.isActive} onChange={v => setF('isActive', v)} />
          <Toggle label="Default" value={form.isDefault} onChange={v => setF('isDefault', v)} />
        </div>
        <p style={{ fontSize: '12px', color: 'rgba(232,239,248,0.6)', margin: '-4px 0 12px' }}>
          ⚠️ <strong>Active means sign-ups are open:</strong> the country appears on the sign-up page and people can register in it.
          Leave it OFF while you prepare the country — cities, routes and prices work with an inactive country.
          Wallet, investments, exchange and P2P stay closed outside Uganda only while the Region country gate is ON.
        </p>
        <div style={{ marginBottom: '12px' }}>
          <Field label="Timezone" value={form.timezone} onChange={v => setF('timezone', v)} placeholder="Africa/Kampala" />
        </div>
      </>
    );

    if (activeSection === 'company') return (
      <>
        <div style={S.row}>
          <Field label="Company Name" value={form.company?.name} onChange={v => setF('company.name', v)} />
          <Field label="Tagline" value={form.company?.tagline} onChange={v => setF('company.tagline', v)} />
        </div>
        <div style={S.row}>
          <Field label="Website" value={form.company?.website} onChange={v => setF('company.website', v)} />
          <Field label="Founded Year" value={form.company?.founded} onChange={v => setF('company.founded', v)} />
        </div>
        <div style={S.row}>
          <Field label="Reg. Number" value={form.company?.regNumber} onChange={v => setF('company.regNumber', v)} />
          <Field label="Logo URL" value={form.company?.logo} onChange={v => setF('company.logo', v)} />
        </div>
      </>
    );

    if (activeSection === 'office') return (
      <>
        <div style={{ marginBottom: '12px' }}>
          <Field label="Street Address" value={form.office?.street} onChange={v => setF('office.street', v)} />
        </div>
        <div style={S.row}>
          <Field label="City" value={form.office?.city} onChange={v => setF('office.city', v)} />
          <Field label="District / State" value={form.office?.district} onChange={v => setF('office.district', v)} />
        </div>
        <div style={S.row}>
          <Field label="Country" value={form.office?.country} onChange={v => setF('office.country', v)} />
          <Field label="Postal Code" value={form.office?.postalCode} onChange={v => setF('office.postalCode', v)} />
        </div>
        <Field label="Google Maps URL" value={form.office?.mapUrl} onChange={v => setF('office.mapUrl', v)} />
      </>
    );

    if (activeSection === 'contact') return (
      <>
        <div style={S.row}>
          <Field label="Main Phone" value={form.contact?.phone} onChange={v => setF('contact.phone', v)} placeholder="+256 700 000000" />
          <Field label="Alt Phone" value={form.contact?.altPhone} onChange={v => setF('contact.altPhone', v)} />
        </div>
        <div style={S.row}>
          <Field label="Email" type="email" value={form.contact?.email} onChange={v => setF('contact.email', v)} />
          <Field label="Support Email" type="email" value={form.contact?.supportEmail} onChange={v => setF('contact.supportEmail', v)} />
        </div>
        <Field label="WhatsApp Number" value={form.contact?.whatsapp} onChange={v => setF('contact.whatsapp', v)} />
      </>
    );

    if (activeSection === 'currency') return (
      <>
        <div style={S.row}>
          <Field label="Currency Code" value={form.currency?.code} onChange={v => setF('currency.code', v.toUpperCase())} placeholder="UGX" />
          <Field label="Currency Name" value={form.currency?.name} onChange={v => setF('currency.name', v)} placeholder="Ugandan Shilling" />
        </div>
        <div style={S.row}>
          <Field label="Symbol" value={form.currency?.symbol} onChange={v => setF('currency.symbol', v)} placeholder="UGX" />
          <Field label="Decimal Places" type="number" value={form.currency?.decimals} onChange={v => setF('currency.decimals', +v)} />
        </div>
      </>
    );

    if (activeSection === 'exchange') return (
      <>
        <div style={S.row}>
          <Field label="Buy Spread %" type="number" value={form.exchange?.buySpreadPct} onChange={v => setF('exchange.buySpreadPct', +v)} />
          <Field label="Sell Spread %" type="number" value={form.exchange?.sellSpreadPct} onChange={v => setF('exchange.sellSpreadPct', +v)} />
        </div>
        <div style={S.row}>
          <Field label="Min Buy USDT" type="number" value={form.exchange?.minBuyUsdt} onChange={v => setF('exchange.minBuyUsdt', +v)} />
          <Field label="Max Buy USDT" type="number" value={form.exchange?.maxBuyUsdt} onChange={v => setF('exchange.maxBuyUsdt', +v)} />
        </div>
        <div style={S.row}>
          <Field label="Min Sell USDT" type="number" value={form.exchange?.minSellUsdt} onChange={v => setF('exchange.minSellUsdt', +v)} />
          <Field label="Max Sell USDT" type="number" value={form.exchange?.maxSellUsdt} onChange={v => setF('exchange.maxSellUsdt', +v)} />
        </div>
        <div style={S.row}>
          <Field label="Withdrawal Fee %" type="number" value={form.exchange?.withdrawalFeePct} onChange={v => setF('exchange.withdrawalFeePct', +v)} />
          <div><Toggle label="Exchange Enabled" value={form.exchange?.enabled} onChange={v => setF('exchange.enabled', v)} /></div>
        </div>
      </>
    );

    if (activeSection === 'referral') return (
      <>
        <div style={S.row}>
          <Field label="Bonus %" type="number" value={form.referral?.bonusPct} onChange={v => setF('referral.bonusPct', +v)} />
          <Field label="Max Levels" type="number" value={form.referral?.maxLevels} onChange={v => setF('referral.maxLevels', +v)} />
        </div>
        <div style={S.row}>
          <Field label="Min Deposit (local currency)" type="number" value={form.referral?.minDeposit} onChange={v => setF('referral.minDeposit', +v)} />
          <div><Toggle label="Referral Enabled" value={form.referral?.enabled} onChange={v => setF('referral.enabled', v)} /></div>
        </div>
      </>
    );

    if (activeSection === 'branding') return (
      <>
        <div style={S.row}>
          <Field label="App Name" value={form.branding?.appName} onChange={v => setF('branding.appName', v)} />
          <Field label="Logo URL" value={form.branding?.logoUrl} onChange={v => setF('branding.logoUrl', v)} />
        </div>
        <div style={S.row3}>
          <div>
            <label style={S.label}>Primary Color</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.branding?.primaryColor || '#0A1628'} onChange={e => setF('branding.primaryColor', e.target.value)} style={{ width: '36px', height: '36px', borderRadius: '6px', border: 'none', cursor: 'pointer', padding: 0 }} />
              <input style={{ ...S.input, flex: 1 }} value={form.branding?.primaryColor} onChange={e => setF('branding.primaryColor', e.target.value)} />
            </div>
          </div>
          <div>
            <label style={S.label}>Accent Color</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.branding?.accentColor || '#00A8DB'} onChange={e => setF('branding.accentColor', e.target.value)} style={{ width: '36px', height: '36px', borderRadius: '6px', border: 'none', cursor: 'pointer', padding: 0 }} />
              <input style={{ ...S.input, flex: 1 }} value={form.branding?.accentColor} onChange={e => setF('branding.accentColor', e.target.value)} />
            </div>
          </div>
          <div>
            <label style={S.label}>Secondary Color</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.branding?.secondaryColor || '#1B61AC'} onChange={e => setF('branding.secondaryColor', e.target.value)} style={{ width: '36px', height: '36px', borderRadius: '6px', border: 'none', cursor: 'pointer', padding: 0 }} />
              <input style={{ ...S.input, flex: 1 }} value={form.branding?.secondaryColor} onChange={e => setF('branding.secondaryColor', e.target.value)} />
            </div>
          </div>
        </div>
      </>
    );

    if (activeSection === 'tax') return (
      <>
        <div style={S.row}>
          <Field label="VAT Rate %" type="number" value={form.tax?.vatRate} onChange={v => setF('tax.vatRate', +v)} />
          <Field label="Withholding Tax %" type="number" value={form.tax?.withholdingRate} onChange={v => setF('tax.withholdingRate', +v)} />
        </div>
        <div style={S.row}>
          <Toggle label="VAT Registered" value={form.tax?.vatRegistered} onChange={v => setF('tax.vatRegistered', v)} />
          <Field label="VAT Number" value={form.tax?.vatNumber} onChange={v => setF('tax.vatNumber', v)} />
        </div>
      </>
    );

    return null;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <span style={{ fontSize: '28px' }}>🌍</span>
        <div>
          <h1 style={S.title}>Countries Management</h1>
          <p style={S.sub}>Configure multi-country settings. Each country has independent config.</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
          {mode !== 'list' && (
            <button style={S.btn('ghost')} onClick={() => setMode('list')}>← Back to List</button>
          )}
          {mode === 'list' && (
            <button style={S.btn('#00A8DB')} onClick={openCreate}>+ Add Country</button>
          )}
        </div>
      </div>

      {/* List view */}
      {mode === 'list' && (
        <div style={S.card}>
          {countries.length === 0 && (
            <p style={{ color: 'rgba(232,239,248,0.4)', textAlign: 'center', padding: '40px' }}>
              No countries configured. Click "Add Country" to get started.
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  {['Code', 'Country', 'Currency', 'Timezone', 'Status', 'Default', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: 'rgba(232,239,248,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {countries.map(c => (
                  <tr key={c._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '12px', fontSize: '13px', fontWeight: 700, color: '#00A8DB' }}>{c.code}</td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>{c.flag}</span>
                      {c.name}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'rgba(232,239,248,0.6)' }}>
                      {c.currency?.code} {c.currency?.symbol && `(${c.currency.symbol})`}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: 'rgba(232,239,248,0.5)' }}>{c.timezone || '—'}</td>
                    <td style={{ padding: '12px' }}><span style={S.badge(c.isActive)}>{c.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{c.isDefault ? '⭐ Default' : '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={{ ...S.btn('ghost'), padding: '5px 10px', fontSize: '12px' }} onClick={() => openEdit(c)}>Edit</button>
                        <button style={{ ...S.btn('ghost'), padding: '5px 10px', fontSize: '12px' }} onClick={() => handleToggleActive(c)}>
                          {c.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        {!c.isDefault && (
                          <button style={{ ...S.btn('red'), padding: '5px 10px', fontSize: '12px' }} onClick={() => handleDelete(c)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit / Create form */}
      {(mode === 'edit' || mode === 'create') && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '20px', alignItems: 'start' }}>
          {/* Sidebar */}
          <div style={S.card}>
            {selected && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '6px' }}>{form.flag || '🌍'}</div>
                <div style={{ fontWeight: 900, color: '#F0F6FF', fontSize: '16px' }}>{form.name || 'New Country'}</div>
                <div style={{ color: 'rgba(232,239,248,0.4)', fontSize: '12px' }}>{form.code}</div>
              </div>
            )}
            {!selected && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '6px' }}>🌐</div>
                <div style={{ fontWeight: 900, color: '#F0F6FF', fontSize: '16px' }}>New Country</div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {SECTIONS.map(s => (
                <button key={s} type="button" onClick={() => setActiveSection(s)}
                  style={{
                    padding: '9px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    textAlign: 'left', fontSize: '13px', fontWeight: activeSection === s ? 700 : 500,
                    background: activeSection === s ? 'rgba(0,168,219,0.15)' : 'transparent',
                    color: activeSection === s ? '#00A8DB' : 'rgba(232,239,248,0.6)',
                    borderLeft: activeSection === s ? '3px solid #00A8DB' : '3px solid transparent',
                  }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Form panel */}
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#F0F6FF' }}>
                {mode === 'create' ? 'New Country' : `Edit: ${selected?.name}`}
              </h2>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ ...S.btn('#00A8DB'), opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : mode === 'create' ? 'Create Country' : 'Save Changes'}
              </button>
            </div>
            {renderSection()}
          </div>
        </div>
      )}
    </div>
  );
}
